import React, { useState, useEffect } from 'react';
import { walletApi } from '../services/walletApi';
import { walletGroupApi } from '../services/walletGroupAPI';
import { formatAddress } from '../utils/formatNames';
import WalletGroupBadge from './WalletGroupBadge';
import WalletDeleteModal from './WalletDeleteModal';
import TokenDisplay from './TokenDisplay';
import WalletFilter from './WalletFilter';
import { Download, FileSpreadsheet } from 'lucide-react';

const WalletList = ({ selectedGroupId, onSelectWallet, selectedWallet, onUpdateGroups, tokenId }) => {
  
  // Debug logging
  console.log('🔍 [WalletList] Component props:', {
    selectedGroupId,
    tokenId,
    pathname: window.location.pathname
  });
  const [wallets, setWallets] = useState([]);
  const [groups, setGroups] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBatchActions, setShowBatchActions] = useState(false);
  const [selectedWallets, setSelectedWallets] = useState([]);
  const [targetGroupId, setTargetGroupId] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [walletCount, setWalletCount] = useState(10);
  const [namePrefix, setNamePrefix] = useState('Wallet');
  const [generating, setGenerating] = useState(false);
  const [deleteModalWallet, setDeleteModalWallet] = useState(null);
  const [walletBalances, setWalletBalances] = useState({});
  const [balancesEnabled] = useState(true); // Always enabled now
  const [filteredWallets, setFilteredWallets] = useState([]);
  const [balancesLoading, setBalancesLoading] = useState(true);
  
  // Fetch wallets, optionally filtered by group
  const fetchWallets = async () => {
    try {
      setLoading(true);
      
      let response;
      
      console.log(`WalletList: Fetching wallets for selectedGroupId: ${selectedGroupId}`);
      
      if (selectedGroupId === 'ungrouped') {
        // Fetch ungrouped wallets
        console.log('WalletList: Fetching ungrouped wallets with tokenId:', tokenId);
        response = await walletApi.getUngroupedWallets(tokenId);
      } else if (selectedGroupId === null || selectedGroupId === undefined) {
        // Fetch all wallets (no group filter)
        console.log('WalletList: Fetching all wallets for token:', tokenId);
        response = await walletApi.getUserWallets(null, tokenId);
      } else {
        // Fetch wallets for specific group - FIXED: Pass groupId as parameter
        console.log(`WalletList: Fetching wallets for group ${selectedGroupId} and token:`, tokenId);
        response = await walletApi.getUserWallets(selectedGroupId, tokenId);
      }
      
      console.log(`WalletList: Received ${response.wallets?.length || 0} wallets for tokenId:`, tokenId);
      console.log('🔍 [WalletList] Full API response:', response);
      
      const walletsData = response.wallets || [];
      setWallets(walletsData);
      setFilteredWallets(walletsData);
      setGroups(response.groups || []);
      
      // Ensure loading is always set to false, even with 0 wallets
      setLoading(false);
      
      // Clear selection when group changes
      setSelectedWallets([]);
      
      // If we have no selected wallet but wallets exist, select the first one
      if (!selectedWallet && response.wallets && response.wallets.length > 0 && onSelectWallet) {
        onSelectWallet(response.wallets[0]);
      }
    } catch (error) {
      console.error('Error fetching wallets:', error);
      setError('Failed to load wallets. Please try again.');
      setWallets([]);
      setFilteredWallets([]);
      setLoading(false);
    }
  };

  // Fetch all available groups
  const fetchAllGroups = async () => {
    try {
      console.log('Fetching all wallet groups...');
      const groupsData = await walletGroupApi.getWalletGroups(tokenId);
      console.log('Fetched groups:', groupsData);
      setAllGroups(groupsData);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  // Fetch all groups when component mounts
  useEffect(() => {
    fetchAllGroups();
  }, []);

  // Fetch all balances at once
  useEffect(() => {
    if (wallets.length === 0) return;
    
    const fetchAllBalances = async () => {
      setBalancesLoading(true);
      
      try {
        // Get all wallet IDs
        const walletIds = wallets.map(w => w.id || w._id);
        
        // Fetch all balances in one request
        const balancesData = await walletApi.getMultipleWalletBalances(walletIds);
        
        // Process the response and update state
        const newBalances = {};
        balancesData.balances.forEach((balance) => {
          // Use walletId from the balance response for proper mapping
          const walletId = balance.walletId;
          if (!walletId) {
            console.error('Balance response missing walletId:', balance);
            return;
          }
          
          if (balance.error) {
            newBalances[walletId] = {
              sol: 0,
              tokens: [],
              error: balance.error,
              fetchedAt: Date.now()
            };
          } else {
            newBalances[walletId] = {
              sol: balance.sol?.balance || 0,
              tokens: balance.tokens || [],
              error: null,
              fetchedAt: Date.now()
            };
          }
        });
        
        setWalletBalances(newBalances);
        setBalancesLoading(false);
        
      } catch (error) {
        console.error('Error fetching all balances:', error);
        setBalancesLoading(false);
        // Set all balances to error state
        const errorBalances = {};
        wallets.forEach(wallet => {
          errorBalances[wallet.id || wallet._id] = {
            sol: 0,
            tokens: [],
            error: 'Failed to load balance',
            fetchedAt: Date.now()
          };
        });
        setWalletBalances(errorBalances);
      }
    };
    
    fetchAllBalances();
  }, [wallets]);

  // Initial load and reload when group changes
  useEffect(() => {
    console.log(`WalletList useEffect: selectedGroupId changed to ${selectedGroupId}, tokenId:`, tokenId);
    // Don't clear balance cache when switching groups - preserve it for better UX
    // setWalletBalances({});
    // setLoadingBalances({});
    if (tokenId) {
      fetchWallets();
    } else {
      // If no tokenId, stop loading and clear wallets
      console.log('No tokenId provided, clearing wallets and stopping loading');
      setLoading(false);
      setWallets([]);
      setFilteredWallets([]);
    }
  }, [selectedGroupId, tokenId]);
  
  // Handle wallet selection
  const handleWalletSelect = (wallet) => {
    if (showBatchActions) {
      // In multi-select mode
      const isSelected = selectedWallets.some(w => w.id === wallet.id);
      
      if (isSelected) {
        setSelectedWallets(selectedWallets.filter(w => w.id !== wallet.id));
      } else {
        setSelectedWallets([...selectedWallets, wallet]);
      }
    } else {
      // Single select mode
      if (onSelectWallet) {
        onSelectWallet(wallet);
      }
    }
  };

  // Handle select all wallets
  const handleSelectAll = () => {
    if (selectedWallets.length === filteredWallets.length) {
      setSelectedWallets([]);
    } else {
      setSelectedWallets([...filteredWallets]);
    }
  };
  
  // Handle generating multiple wallets
  const handleGenerateWallets = async (e) => {
    e.preventDefault();
    
    if (!walletCount || walletCount < 1 || walletCount > 1000) {
      setError('Please enter a valid number between 1 and 1000');
      return;
    }
    
    try {
      setGenerating(true);
      
      const result = await walletApi.generateMultipleWallets(
        walletCount, 
        namePrefix, 
        selectedGroupId === 'ungrouped' ? null : selectedGroupId,
        !selectedGroupId || selectedGroupId === 'ungrouped',
        `${namePrefix} Group (${new Date().toLocaleString()})`,
        tokenId
      );
      
      // Reset form
      setShowGenerateForm(false);
      
      // Refresh wallets
      await fetchWallets();
      
      // Notify parent to update groups if needed
      if (onUpdateGroups) {
        onUpdateGroups();
      }
      
      // Show success message
      setError(`Successfully generated ${result.count} wallets.`);
    } catch (error) {
      console.error('Error generating wallets:', error);
      setError('Failed to generate wallets. Please try again.');
    } finally {
      setGenerating(false);
    }
  };
  
  // Quick action: Move selected wallets to a group
  const handleQuickMoveToGroup = async (groupId) => {
    if (!selectedWallets.length) return;
    
    try {
      setLoading(true);
      
      // First remove from current group if they're in a specific group
      if (selectedGroupId && selectedGroupId !== 'ungrouped' && selectedGroupId !== null) {
        await walletApi.removeWalletsFromGroup(
          selectedGroupId,
          selectedWallets.map(w => w.id)
        );
      }
      
      // Then add to new group
      if (groupId !== 'ungrouped') {
        await walletApi.addWalletsToGroup(
          groupId,
          selectedWallets.map(w => w.id)
        );
      }
      
      // Refresh wallets
      await fetchWallets();
      
      // Clear selection
      setSelectedWallets([]);
      setShowBatchActions(false);
      
      // Notify parent to update groups
      if (onUpdateGroups) {
        onUpdateGroups();
      }
    } catch (error) {
      console.error('Error moving wallets:', error);
      setError('Failed to move wallets. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Add selected wallets to a group (keeping existing groups)
  const handleAddToGroup = async () => {
    if (!targetGroupId || selectedWallets.length === 0) return;
    
    try {
      setLoading(true);
      
      await walletApi.addWalletsToGroup(
        targetGroupId,
        selectedWallets.map(w => w.id)
      );
      
      // Refresh wallets and groups
      await fetchWallets();
      await fetchAllGroups();
      
      // Clear selection
      setSelectedWallets([]);
      setShowBatchActions(false);
      setTargetGroupId('');
      
      // Notify parent to update groups if needed
      if (onUpdateGroups) {
        onUpdateGroups();
      }
    } catch (error) {
      console.error('Error adding wallets to group:', error);
      setError('Failed to add wallets to group. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Remove selected wallets from current group
  const handleRemoveFromGroup = async () => {
    if (!selectedGroupId || selectedGroupId === 'ungrouped' || selectedWallets.length === 0) return;
    
    try {
      setLoading(true);
      
      await walletApi.removeWalletsFromGroup(
        selectedGroupId,
        selectedWallets.map(w => w.id)
      );
      
      // Refresh wallets
      await fetchWallets();
      
      // Clear selection
      setSelectedWallets([]);
      setShowBatchActions(false);
      setConfirmRemove(false);
      
      // Notify parent to update groups if needed
      if (onUpdateGroups) {
        onUpdateGroups();
      }
    } catch (error) {
      console.error('Error removing wallets from group:', error);
      setError('Failed to remove wallets from group. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Export wallets
  const handleExportWallets = async () => {
    try {
      await walletApi.exportWalletsToExcel(selectedGroupId);
    } catch (error) {
      console.error('Error exporting wallets:', error);
      setError('Failed to export wallets. Please try again.');
    }
  };
  
  // Handle wallet deletion
  const handleDeleteWallet = async (wallet) => {
    try {
      setLoading(true);
      await walletApi.deleteWallet(wallet.id);
      
      // Close modal
      setDeleteModalWallet(null);
      
      // Refresh wallets
      await fetchWallets();
      
      // If the deleted wallet was selected, clear selection
      if (selectedWallet && selectedWallet.id === wallet.id) {
        onSelectWallet(null);
      }
      
      // Show success message
      setError(`Successfully deleted wallet "${wallet.name || `Wallet ${wallet.id}`}"`);
      
      // Notify parent to update groups if needed
      if (onUpdateGroups) {
        onUpdateGroups();
      }
    } catch (error) {
      console.error('Error deleting wallet:', error);
      setError('Failed to delete wallet. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Get display name for current view
  const getDisplayName = () => {
    if (selectedGroupId === null || selectedGroupId === undefined) {
      return 'All Wallets';
    } else if (selectedGroupId === 'ungrouped') {
      return 'Ungrouped Wallets';
    } else {
      const group = groups.find(g => g.id === selectedGroupId);
      return group ? group.name : 'Wallets';
    }
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3 sm:p-4 flex flex-col h-full transition-colors">
      {/* Header with title and export button */}
      <div className="flex justify-between items-start mb-3 sm:mb-4">
        <h2 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-100">
          {getDisplayName()}
          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">({filteredWallets.length})</span>
        </h2>
        {/* Export button positioned at top right */}
        <button
          onClick={handleExportWallets}
          className="group inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-green-600 dark:text-green-400 rounded-lg font-medium text-sm shadow-sm hover:shadow-md transition-all duration-200 border border-gray-200 dark:border-gray-600"
          title="Export wallets to Excel"
        >
          <FileSpreadsheet className="w-4 h-4" />
          <span className="hidden sm:inline">Export</span>
          <Download className="w-3 h-3 opacity-60" />
        </button>
      </div>

      {/* Action buttons row */}
      <div className="flex flex-wrap gap-2 mb-3">
        <button
          onClick={() => setShowGenerateForm(!showGenerateForm)}
          className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
        >
          <span className="hidden xs:inline">+ Generate</span>
          <span className="xs:hidden">+</span>
        </button>
        <button
          onClick={() => setShowBatchActions(!showBatchActions)}
          className={`px-2 sm:px-3 py-1 text-xs sm:text-sm ${
            showBatchActions
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
          } rounded transition-colors`}
        >
          <span className="hidden sm:inline">{showBatchActions ? 'Cancel' : 'Select Multiple'}</span>
          <span className="sm:hidden">{showBatchActions ? 'Cancel' : 'Select'}</span>
        </button>
        <WalletFilter
          wallets={wallets}
          walletBalances={walletBalances}
          onFilterChange={setFilteredWallets}
          balancesEnabled={balancesEnabled}
          isLoading={balancesLoading}
        />
      </div>
      
      {error && (
        <div className={`${
          error.includes('Successfully') 
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' 
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
        } p-3 rounded mb-4 text-sm transition-colors`}>
          {error}
          <button 
            onClick={() => setError(null)} 
            className={`ml-2 ${
              error.includes('Successfully') 
                ? 'text-green-500 dark:text-green-400 hover:text-green-700 dark:hover:text-green-200' 
                : 'text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-200'
            }`}
          >
            ×
          </button>
        </div>
      )}
      
      {/* Generate Wallets Form */}
      {showGenerateForm && (
        <form onSubmit={handleGenerateWallets} className="mb-3 sm:mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 transition-colors">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Generate Multiple Wallets</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Number of Wallets (1-1000)</label>
              <input
                type="number"
                value={walletCount}
                onChange={(e) => setWalletCount(parseInt(e.target.value) || 1)}
                min="1"
                max="1000"
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Name Prefix</label>
              <input
                type="text"
                value={namePrefix}
                onChange={(e) => setNamePrefix(e.target.value)}
                placeholder="Wallet Name Prefix"
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                required
              />
            </div>
          </div>
          <div className="flex flex-col xs:flex-row xs:justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowGenerateForm(false)}
              className="px-3 py-2 text-sm bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors order-2 xs:order-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={generating || loading}
              className="px-3 py-2 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors disabled:bg-purple-400 order-1 xs:order-2"
            >
              {generating ? 'Generating...' : `Generate ${walletCount} Wallets`}
            </button>
          </div>
        </form>
      )}
      
      {/* Enhanced Batch Actions Bar */}
      {showBatchActions && (
        <div className="mb-3 sm:mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-700 transition-colors">
          <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between mb-2 gap-2 xs:gap-0">
            <div className="flex flex-col xs:flex-row xs:items-center gap-2 xs:gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedWallets.length === filteredWallets.length && filteredWallets.length > 0}
                  onChange={handleSelectAll}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Select All</span>
              </label>
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                {selectedWallets.length} selected
              </span>
            </div>
            <button
              onClick={() => {
                setShowBatchActions(false);
                setSelectedWallets([]);
              }}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
          
          {selectedWallets.length > 0 && (
            <div className="space-y-2">
              {/* Quick Actions */}
              <div className="space-y-2">
                <div className="text-xs text-blue-700 dark:text-blue-300 font-medium">Quick Move:</div>
                <div className="flex flex-wrap gap-1 sm:gap-2">
                  <button
                    onClick={() => handleQuickMoveToGroup('ungrouped')}
                    className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    → Ungrouped
                  </button>
                  {groups.map(group => (
                    <button
                      key={group.id}
                      onClick={() => handleQuickMoveToGroup(group.id)}
                      className="px-2 py-1 text-xs rounded hover:opacity-80 transition-colors text-white truncate max-w-[120px]"
                      style={{ backgroundColor: group.color || '#4C6FFF' }}
                      title={`Move to ${group.name}`}
                    >
                      → {group.name}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Traditional Actions */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2 pt-2 border-t border-blue-200 dark:border-blue-700">
                {selectedGroupId && selectedGroupId !== 'ungrouped' && (
                  <button
                    onClick={() => setConfirmRemove(true)}
                    className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                  >
                    Remove from Group
                  </button>
                )}
                <div className="flex items-center gap-2">
                  <select
                    value={targetGroupId || ''}
                    onChange={(e) => setTargetGroupId(e.target.value)}
                    className="flex-1 sm:flex-none p-1 text-xs border border-blue-300 dark:border-blue-600 rounded bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300 transition-colors"
                  >
                    <option value="">Add to Group...</option>
                    {allGroups && allGroups.length > 0 ? (
                      allGroups.filter(group => group.id !== selectedGroupId).map(group => (
                        <option key={group.id} value={group.id}>{group.name}</option>
                      ))
                    ) : null}
                  </select>
                  {targetGroupId && (
                    <button
                      onClick={handleAddToGroup}
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      Add
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Remove from Group Confirmation */}
      {confirmRemove && (
        <div className="mb-4 p-3 bg-red-50 rounded border border-red-200">
          <p className="text-sm text-red-700 mb-2">
            Are you sure you want to remove {selectedWallets.length} wallets from this group?
            The wallets will still exist but won't be in this group anymore.
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setConfirmRemove(false)}
              className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded mr-2 hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRemoveFromGroup}
              disabled={loading}
              className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:bg-red-400"
            >
              {loading ? 'Removing...' : 'Remove from Group'}
            </button>
          </div>
        </div>
      )}
      
      {/* Balance Loading Indicator */}
      {balancesLoading && wallets.length > 0 && (
        <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-center space-x-3">
            <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin"></div>
            <div className="flex-1">
              <span className="text-sm font-medium text-purple-700">
                Loading balances for {wallets.length} wallets...
              </span>
              <p className="text-xs text-purple-600 mt-1">
                This may take a moment for large collections
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Wallet List - Flexible height with improved scrollability */}
      <div className="space-y-1 sm:space-y-2 overflow-y-auto pr-1 sm:pr-2 pb-4 flex-1" style={{ minHeight: '200px' }}>
        {filteredWallets.map(wallet => (
          <div
            key={wallet.id}
            className={`flex items-start justify-between p-2 sm:p-3 md:p-4 rounded-lg cursor-pointer transition-all
              ${(showBatchActions && selectedWallets.some(w => w.id === wallet.id))
                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-400 dark:border-blue-600 border'
                : (!showBatchActions && selectedWallet && selectedWallet.id === wallet.id)
                  ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-400 dark:border-purple-600 border'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent'}`}
            onClick={() => handleWalletSelect(wallet)}
          >
            <div className="flex items-start w-full">
              {showBatchActions && (
                <input
                  type="checkbox"
                  checked={selectedWallets.some(w => w.id === wallet.id)}
                  onChange={() => handleWalletSelect(wallet)}
                  className="mr-2 sm:mr-3 mt-1 flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <div className="flex flex-col flex-1 min-w-0">
                <div className="flex items-center">
                  <span className="font-medium text-sm sm:text-base text-gray-900 dark:text-gray-100 truncate">
                    {wallet.name || `Wallet ${wallet.id}`}
                  </span>
                  {wallet.is_default && (
                    <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-1.5 sm:px-2 py-0.5 rounded-full ml-2 flex-shrink-0">Default</span>
                  )}
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">{formatAddress(wallet.address)}</span>
                
                {/* Show wallet groups as badges */}
                {!showBatchActions && wallet.groups && wallet.groups.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {wallet.groups.map(group => (
                      <WalletGroupBadge key={group.id} group={group} size="xs" showName={false} />
                    ))}
                  </div>
                )}
                
                {/* Token Balance Display */}
                {!showBatchActions && (
                  <TokenDisplay
                    solBalance={walletBalances[wallet.id || wallet._id]?.sol}
                    tokens={walletBalances[wallet.id || wallet._id]?.tokens || []}
                    loading={balancesLoading && !walletBalances[wallet.id || wallet._id]}
                    error={walletBalances[wallet.id || wallet._id]?.error}
                  />
                )}
              </div>
            </div>
            
            {/* Wallet Actions - Only show when not in batch mode */}
            {!showBatchActions && (
              <div className="flex space-x-1 ml-1 sm:ml-2 flex-shrink-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Copy address to clipboard
                    navigator.clipboard.writeText(wallet.address)
                      .then(() => {
                        // Show a temporary success message
                        const tempEl = document.createElement('div');
                        tempEl.className = 'fixed bottom-4 right-4 bg-gray-900 text-white px-4 py-2 rounded shadow-lg text-sm z-50';
                        tempEl.textContent = 'Address copied!';
                        document.body.appendChild(tempEl);
                        setTimeout(() => {
                          document.body.removeChild(tempEl);
                        }, 2000);
                      })
                      .catch(err => {
                        console.error('Failed to copy address:', err);
                      });
                  }}
                  className="p-1 text-gray-500 dark:text-gray-400 hover:text-blue-700 dark:hover:text-blue-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Copy address"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Show modal without private key (private keys no longer accessible)
                    setDeleteModalWallet(wallet);
                  }}
                  className="p-1 text-gray-500 dark:text-gray-400 hover:text-red-700 dark:hover:text-red-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Delete wallet"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        ))}
        
        {filteredWallets.length === 0 && !loading && (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            {selectedGroupId === null || selectedGroupId === undefined
              ? 'No wallets found. Generate wallets to get started.'
              : selectedGroupId === 'ungrouped'
                ? 'No ungrouped wallets found.'
                : 'No wallets in this group. Add wallets to this group or generate new ones.'}
          </div>
        )}
        
        {loading && filteredWallets.length === 0 && (
          <div className="p-8 flex justify-center">
            <div className="w-8 h-8 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin"></div>
          </div>
        )}
      </div>
      
      {/* Delete Wallet Modal */}
      {deleteModalWallet && (
        <WalletDeleteModal
          wallet={deleteModalWallet}
          onClose={() => setDeleteModalWallet(null)}
          onConfirm={handleDeleteWallet}
        />
      )}
    </div>
  );
};

export default WalletList;